import { Constructor, Nullable, PartialNullable } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { $$, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { ContainerControlStatic } from "./ContainerControlStatic";
import { Control, ControlConstructor, ControlPropertyValues, FOCUSABLE_ELEMENTS_SELECTOR, IControl } from "./Control";
import { NAMESPACE } from "./_Namespace";

export type ContainerControlPropertyValues =
    ControlPropertyValues
    & {
        autoFocusChildElement: boolean;
    };

export type ContainerControlProperties = {
    [name in keyof ContainerControlPropertyValues]: Property<ContainerControlPropertyValues[name]>
};

export interface IContainerControl<TChildControl extends IControl = IControl> extends IControl, ContainerControlProperties
{
    _containerElementRef: ElementRef;

    append($$elementOrControlOrArrayOf: JNative | TChildControl | TChildControl[], beforeSelector?: any): this;
    controlsClear(detach?: boolean): void;
    controlsGet(recursive?: boolean): TChildControl[];
    prepend($$elementOrControlOrArrayOf: JNative | TChildControl | TChildControl[], beforeSelector?: any): this;
}

export type ContainerControlConstructor = ControlConstructor & {
    getAllowedChildControlClasses(): (typeof Control)[];
}

export class ContainerControl<TPropertyValues extends ContainerControlPropertyValues = ContainerControlPropertyValues, TChildControl extends IControl = IControl> extends Control<TPropertyValues> implements IContainerControl<TChildControl>
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ContainerControl";
    public static override className = "ContainerControl";

    // #region Public Methods

    public static getAllowedChildControlClasses(): (typeof Control<ControlPropertyValues>)[]
    {
        return [Control];
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ContainerControlPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                autoFocusChildElement: true
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public _containerElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public autoFocusChildElement!: PropertyBoolean;

    // #endregion

    // #region Public Methods

    public append($$elementOrControlOrArrayOf: JNative | TChildControl | TChildControl[], beforeSelector?: any): this
    {
        const result = this._appendOrPrepend($$elementOrControlOrArrayOf, true, beforeSelector);
        ContainerControlStatic._triggerControlsChanged(this);
        return result;
    }
    public controlsClear(detach?: boolean): void
    {
        this.controlsGet()
            .forEach(item =>
            {
                if (detach)
                {
                    item.detach();
                    return;
                }

                item.remove();
            });

        ContainerControlStatic._triggerControlsChanged(this);
    }
    public controlsGet(recursive?: boolean): TChildControl[]
    public controlsGet<TChildControl2 extends TChildControl = TChildControl>(recursive: boolean | undefined, ofType: Constructor<TChildControl2>): TChildControl2[]
    public controlsGet<TChildControl2 extends TChildControl = TChildControl>(recursive?: boolean, ofType?: Constructor<TChildControl2>): TChildControl[]
    {
        const
            controls = [],
            $$children = this._containerElementRef?.current.children();

        if ($$children)
        {
            for (let child of $$children)
            {
                if (!(child instanceof HTMLElement))
                    continue;

                const
                    control = JNativeStatic.radControl(child);

                if (!control)
                    continue;

                if (control instanceof Control)
                    controls.push(control);

                if (recursive && control instanceof ContainerControl)
                {
                    const
                        subChildren = control.controlsGet(true);

                    for (let subChild of subChildren)
                        controls.push(subChild);
                }
            }
        }

        return (
            ofType
                ? controls.filter((item): item is TChildControl2 => Object.getPrototypeOf(item).constructor === ofType)
                : controls
        );
    }
    public prepend($$elementOrControlOrArrayOf: JNative | TChildControl | TChildControl[], beforeSelector?: any): this
    {
        const result = this._appendOrPrepend($$elementOrControlOrArrayOf, false, beforeSelector);
        ContainerControlStatic._triggerControlsChanged(this);
        return result;
    }

    // #endregion

    // #region Protected Methods

    protected _$$containerElementChanged(change: PropertyChange<JNative<HTMLElement>>)
    {
        /* Intentionally empty */
    }
    protected _appendOrPrepend($$elementOrControlOrArrayOf: JNative | TChildControl | TChildControl[], append: boolean, beforeSelector?: any)
    {
        const
            containerElement = this._containerElementRef.current.get(0);

        if (!containerElement)
            return this;

        const
            elementOrControls = $$($$elementOrControlOrArrayOf),
            elementsToAdd = [];

        for (let elementOrControl of elementOrControls)
        {
            if (ArrayHelper.isArrayLike(elementOrControl))
                elementOrControl = ArrayHelper.firstOrDefault(elementOrControl);

            if (!elementOrControl)
                continue;

            let
                elementToAdd: { control?: Control, element?: HTMLElement, originalParent?: Nullable<HTMLElement> } = {};

            if (elementOrControl instanceof HTMLElement)
            {
                elementToAdd.control = $$(elementOrControl).radControl();
                elementToAdd.element = elementOrControl;
            }
            else if (elementOrControl instanceof Control)
            {
                elementToAdd.control = elementOrControl;
                elementToAdd.element = elementToAdd.control.$$element.get()?.get(0);
            }
            else
                throw new Error("Only Elements and Controls are supported.");

            if (elementToAdd.control && !this._isAllowedControlClass(elementToAdd.control))
                throw new Error("The supplied Control is not of an accepted type.");

            elementToAdd.originalParent = elementToAdd.element?.parentElement;
            elementsToAdd.push(elementToAdd);
        }

        if (elementsToAdd.length)
        {
            let
                beforeElements;

            if (beforeSelector)
                beforeElements = containerElement.querySelectorAll(beforeSelector);

            if (beforeElements && beforeElements.length)
            {
                for (let elementToAdd of elementsToAdd)
                {
                    if (!elementToAdd.element)
                        continue;

                    containerElement.insertBefore(elementToAdd.element, beforeElements[0]);
                }
            }
            else if (append)
                containerElement.append.apply(containerElement, elementsToAdd.filter(item => !!item.element).map(item => item.element!));
            else
                containerElement.prepend.apply(containerElement, elementsToAdd.filter(item => !!item.element).map(item => item.element!));

            elementsToAdd
                .filter(item => !!item.element)
                .map(item => [item.element].concat(Array.from(item.element!.querySelectorAll(".rad-control") as unknown as any[])))
                .reduce((a, b) => a.concat(b), [])
                .forEach(item => { JNativeStatic.radControl(item!)?.triggerHandler("ancestorChanged") });

            elementsToAdd
                .filter(item => !!item.control)
                .forEach(item => { item.control!.triggerHandler("parentChanged", { originalValue: item.originalParent ? $$(item.originalParent) : null, currentValue: $$(containerElement) }) });
        }

        return this;
    }
    protected _autoFocusChildElementChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _createChildrenHtml(): JsxFragment | string | (JsxFragment | string | undefined)[] | undefined
    {
        return undefined;
    }
    protected _isAllowedControlClass(control: IControl)
    {
        const
            constructor = Object.getPrototypeOf(this).constructor as Constructor<Control>,
            allowedControlClasses = (constructor as any).allowedChildControlClasses as Constructor<Control>[];

        if (!allowedControlClasses)
            return true;

        return (
            ArrayHelper
                .firstOrDefault(allowedControlClasses, item => control instanceof item) !== null
        );
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._containerElementRef = new ElementRef();

        this.autoFocusChildElement = new PropertyBoolean(this, propertyValues.autoFocusChildElement, this._autoFocusChildElementChanged);
    }

    protected override _createElementComplete()
    {
        super._createElementComplete();

        this.addCssClass("rad-control-container-control");
        this._containerElementRef.current.addClass("rad-control-container");
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div ref={this._containerElementRef}>
                {children ?? this._createChildrenHtml()}
            </div>
        );
    }
    protected override _designModeChanged(change: PropertyChange<boolean>)
    {
        super._designModeChanged(change);

        this.controlsGet()
            .forEach(item =>
            {
                item.designMode.set(change.currentValue);
            });
    }

    public override focus()
    {
        const
            autoFocusChildElement = this.autoFocusChildElement.get(),
            $$element = this.$$element.get(),
            element = ($$element?.length ? $$element[0] : null);

        if (!autoFocusChildElement || element?.hasAttribute("tabindex"))
        {
            super.focus();
            return;
        }

        const
            focusableElement = this._containerElementRef.current.find(FOCUSABLE_ELEMENTS_SELECTOR + ", [tabindex]").first();

        if (focusableElement && focusableElement.length)
        {
            const
                control = focusableElement.radControl();

            if (control)
                control.focus();
            else
                focusableElement.focus();
        }
    }
    public override remove()
    {
        for (let control of this.controlsGet())
            control.remove();

        super.remove();
    }

    // #endregion

    // #region Event Handlers

    // #endregion

    // #endregion
}
